import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Canonical wrapper for a dashboard page / tab body. Encodes the standard page
 * anatomy so new pages stay consistent without re-deriving gutters, width, and
 * rhythm every time:
 *
 *   <main pt-[4.5rem]>          ← d/layout.tsx clears the fixed header
 *     <HomeShell>               ← greeting + nav tabs (tab pages only)
 *       <PageContainer>         ← THIS: gutters · max-width · bottom pad · rhythm
 *         <SectionPanel/>       ← each top-level section
 *         <SectionPanel/>
 *
 * Spacing scale baked in (single source of truth — change here, every page
 * follows):
 *  - Horizontal gutters: px-4 sm:px-6 lg:px-8 (app-wide standard).
 *  - `width`: max content width — defaults to 'wide' (6xl), the dashboard-tab norm.
 *  - Bottom padding: pb-12 so the last section clears the viewport bottom.
 *  - `stack` (default true): vertical rhythm between DIRECT children via space-y,
 *    so sections never hand-roll mb-* AND the first child has no top gap (it sits
 *    flush under the tabs, matching Home). Turn off for a page that manages its
 *    own internal spacing (e.g. a single full-height workspace).
 *
 * There is intentionally NO top margin: tab pages sit under HomeShell's small
 * pt; standalone pages sit under the layout's header clearance.
 *
 * Built with `cn` (twMerge) so `width` / `className` overrides actually win —
 * unlike the legacy <Container> (clsx), where a passed max-w-* silently lost to
 * its baked-in max-w-7xl.
 *
 * ⚠️ Width-matching caveat: because of that <Container> bug, EVERY existing
 * dashboard page (and the nav tabs in HomeShell) currently renders at max-w-7xl
 * even though they pass max-w-6xl. So to line up visually with the rest of the
 * app today, pass `width="full"`. The intended canonical is 'wide' (6xl) — once
 * <Container> is migrated to `cn` and the app settles on 6xl, drop back to the
 * default. Until then, 'wide' will look NARROWER than its neighbours.
 */

export type PageWidth = 'prose' | 'narrow' | 'wide' | 'full';

/** max-width per semantic width. The label says intent; the value is the cap. */
const WIDTH_CLASS: Record<PageWidth, string> = {
  prose: 'max-w-3xl', // dense forms / settings
  narrow: 'max-w-4xl', // reading / onboarding / pitch
  wide: 'max-w-6xl', // default dashboard tabs (Home, Search, Automations…)
  full: 'max-w-7xl', // edge-to-edge workspaces
};

export interface PageContainerProps {
  children: ReactNode;
  /** Max content width. Defaults to 'wide' (max-w-6xl). */
  width?: PageWidth;
  /** Apply the standard vertical rhythm between direct children. Default true. */
  stack?: boolean;
  className?: string;
}

export function PageContainer({
  children,
  width = 'wide',
  stack = true,
  className,
}: PageContainerProps) {
  return (
    <div
      className={cn(
        'mx-auto w-full px-4 pb-12 sm:px-6 lg:px-8',
        WIDTH_CLASS[width],
        // Canonical section rhythm: 20px mobile / 24px from sm up.
        stack && 'space-y-5 sm:space-y-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
